import { useState, useRef, useCallback, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { showToast } from '@/components/ui/show-toast';
import { useAppSearchParams } from '@/hooks/use-app-search-params';
import { useUpdateOrganisationMutation } from '@/services/organisations/queries';
import { useUploadFileMutation } from '@/services/files/queries';
import type { Organisation } from '@/services/types';

// Constants
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg'];

const formSchema = z.object({
  name: z
    .string()
    .min(2, 'Organization name must be at least 2 characters'),
  logo: z.union([
    z
      .instanceof(File)
      .refine(
        (file) => file.size <= MAX_FILE_SIZE,
        'File size should not exceed 2MB'
      )
      .refine(
        (file) => ACCEPTED_FILE_TYPES.includes(file.type),
        'Only PNG or JPG files are allowed'
      ),
    z.string().min(1, 'Logo is required') // For pre-filled URLs
  ]),
  yearFounded: z.date({ message: 'Please select a date' }).optional(),
  website: z
    .string()
    .transform((val) => {
      if (!val) return ''; // handle empty
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`; // auto-add https if missing
      }
      return val;
    })
    .refine(
      (val) => {
        try {
          if (!val) return true;
          new URL(val);
          return true;
        } catch {
          return false;
        }
      },
      {
        message: 'Please enter a valid URL'
      }
    )
    .optional(),
  linkedInUrl: z
    .string()
    .transform((val) => {
      if (!val) return '';
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`;
      }
      return val;
    })
    .refine(
      (val) => {
        if (!val) return true;
        try {
          new URL(val);
          return true;
        } catch {
          return false;
        }
      },
      {
        message: 'Please enter a valid URL'
      }
    )
    .optional(),
  bio: z
    .string()
    .max(500, 'Bio must not exceed 500 characters')
    .optional()
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  data: Organisation | undefined;
  componentType?: 'verify' | 'edit';
};

export default function CompanyInformationForm({ data, componentType = 'verify' }: Props) {
  const [logoPreview, setLogoPreview] = useState<string | null>(
    data?.logo || null
  );

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(data?.logo || null);
  const [hasLogoChanged, setHasLogoChanged] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const params = useAppSearchParams();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: data?.name || '',
      website: data?.website || '',
      linkedInUrl: data?.linkedInUrl || '',
      yearFounded: data?.yearFounded ? new Date(data.yearFounded) : undefined,
      bio: data?.bio || '',
      logo: data?.logo || ''
    },
    mode: 'onBlur'
  });

  // Mutations
  const { mutate: uploadFile, isPending: isUploadingFile } = useUploadFileMutation(
    (response) => {
      setLogoUrl(response.url);
      updateOrganisationWithData(response.url);
    },
    () => {
      showToast('error', 'Failed to upload logo. Please try again.');
    }
  );

  const { mutate: updateOrganisation, isPending: isUpdatingOrganisation } = useUpdateOrganisationMutation(
    data?.id || '',
    () => {
      showToast('success', 'Company information updated successfully');
      params.set({ view: 'contact-info' });
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update company information. Please try again.');
    }
  );

  // Set initial logo preview if logo exists
  useEffect(() => {
    if (data?.logo) {
      setLogoPreview(data.logo);
      // Initialize the form with the existing logo URL
      form.setValue('logo', data.logo);
    }
  }, [data?.logo, form]);

  // Handle file validation and preview
  const handleFile = useCallback(
    (file: File) => {
      if (!file) return false;

      if (file.size > MAX_FILE_SIZE) {
        form.setError('logo', { message: 'File size should not exceed 2MB' });
        return false;
      }

      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        form.setError('logo', { message: 'Only PNG or JPG files are allowed' });
        return false;
      }

      setLogoFile(file);
      setHasLogoChanged(true);
      form.setValue('logo', file, { shouldValidate: true });

      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);

      return true;
    },
    [form]
  );


  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const updateOrganisationWithData = useCallback((uploadedLogoUrl: string) => {
    const values = form.getValues();
    
    // Prepare organisation update data
    const updateData: any = {
      name: values.name,
      yearFounded: values.yearFounded,
    };

    // Add logo URL
    if (uploadedLogoUrl) {
      updateData.logo = uploadedLogoUrl;
    }

    // Add optional fields
    if (values.website) {
      updateData.website = values.website;
    }
    if (values.linkedInUrl) {
      updateData.linkedInUrl = values.linkedInUrl;
    }
    if (values.bio) {
      updateData.bio = values.bio;
    }

    updateOrganisation(updateData);
  }, [form]);

  const onSubmit = async () => {
    if (!data?.id) {
      showToast('error', 'Organization data not available');
      return;
    }

    try {
      // Only upload if user has changed the logo
      if (hasLogoChanged && logoFile) {
        // Use the uploadFile mutation and update the organisation in the success callback
        uploadFile({
          file: logoFile,
          name: 'logo',
          owner: {
            id: data.id,
            resourceType: 'Organisation'
          }
        });
      } else {
        updateOrganisationWithData(logoUrl || '');
      }
    } catch (error) {
      showToast('error', 'Failed to update company information. Please try again.');
    }
  };

  return (
    <div className={`w-full max-w-3xl overflow-y-auto p-6 font-grotesk ${componentType === 'verify' ? 'mx-auto' : ''}`}>
      <header className='mb-8'>
        <h1 className='font-grotesk text-2xl font-bold'>Company Information</h1>
        <p className='mt-2 text-[#545454]'>Tell us about your organization</p>
      </header>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          {/* Organization Name (Required) */}
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Organization Name *
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='Enter your organization name'
                    {...field}
                    className='bg-gray-50'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Logo Upload (Required) */}
          <FormField
            control={form.control}
            name='logo'
            render={() => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Logo *
                </FormLabel>
                <FormControl>
                  <div
                    className='flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 p-6'
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {logoPreview ? (
                      <div className='flex flex-col items-center gap-4'>
                        <img
                          src={logoPreview}
                          alt='Logo preview'
                          className='max-h-40 max-w-40 object-contain'
                        />
                        <p className='text-sm text-gray-500'>
                          {hasLogoChanged ? 'Click or drag to replace' : 'Click or drag to change logo'}
                        </p>
                      </div>
                    ) : (
                      <div className='flex flex-col items-center gap-2'>
                        <Upload className='h-10 w-10 text-gray-400' />
                        <p className='text-center font-medium'>
                          Select a PNG or JPG file to upload. Max size: 2MB
                        </p>
                        <p className='text-sm text-gray-500'>
                          or drag and drop it here
                        </p>
                      </div>
                    )}
                    <input
                      type='file'
                      ref={fileInputRef}
                      accept={ACCEPTED_FILE_TYPES.join(',')}
                      className='hidden'
                      onChange={handleFileInput}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Website URL (Optional) */}
          <FormField
            control={form.control}
            name='website'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Website URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='https://'
                    {...field}
                    className='bg-gray-50'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* LinkedIn URL (Optional) */}
          <FormField
            control={form.control}
            name='linkedInUrl'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  LinkedIn URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='https://'
                    {...field}
                    className='bg-gray-50'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Year Founded (Required) */}
          <FormField
            control={form.control}
            name='yearFounded'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Year Founded *
                </FormLabel>
                <FormControl>
                  <Input
                    type='date'
                    className='w-full bg-gray-50'
                    {...field}
                    value={
                      field.value ? field.value.toISOString().split('T')[0] : ''
                    }
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? new Date(e.target.value) : null
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Company Bio (Optional) */}
          <FormField
            control={form.control}
            name='bio'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Company Bio
                </FormLabel>
                <FormDescription>Tell us about your company</FormDescription>
                <FormControl>
                  <Textarea
                    placeholder='Write a short bio here'
                    className='h-32 resize-none bg-gray-50'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type='submit'
            className='h-10 w-full'
            disabled={isUploadingFile || isUpdatingOrganisation}
          >
            {isUploadingFile ? 'Uploading Logo...' : isUpdatingOrganisation ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
